import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001'

export interface AuthUser {
  id: string
  email?: string
  roles?: string[]
  nombre_visible?: string
}

export function useAuth() {
  const {
    isAuthenticated,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
    loginWithRedirect,
    logout,
  } = useAuth0()
  const [user, setUser] = useState<AuthUser | undefined>(undefined)
  const [isMeLoading, setIsMeLoading] = useState(true)
  // Auth0 autenticó a la persona (sesión válida), pero no hay fila `usuario` para su auth0_id: puede
  // pasar de verdad (usuario dado de baja) o porque pruebas y producción comparten tenant de Auth0 y la
  // sesión de uno se cuela en el otro sin pedir contraseña.
  const [noAccount, setNoAccount] = useState(false)

  useEffect(() => {
    if (isAuth0Loading) return

    if (!isAuthenticated) {
      setUser(undefined)
      setNoAccount(false)
      setIsMeLoading(false)
      return
    }

    let cancelled = false
    setIsMeLoading(true)

    async function fetchMe() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) {
          if (!cancelled) {
            setUser(undefined)
            setNoAccount(res.status === 404)
          }
          return
        }
        const data: AuthUser = await res.json()
        if (!cancelled) {
          setUser(data)
          setNoAccount(false)
        }
      } catch {
        if (!cancelled) setUser(undefined)
      } finally {
        if (!cancelled) setIsMeLoading(false)
      }
    }

    fetchMe()

    return () => {
      cancelled = true
    }
  }, [isAuthenticated, isAuth0Loading, getAccessTokenSilently])

  return {
    isAuthenticated,
    isLoading: isAuth0Loading || (isAuthenticated && isMeLoading),
    user,
    noAccount,
    login: (returnTo?: string) => loginWithRedirect(returnTo ? { appState: { returnTo } } : undefined),
    logout: () => logout({ logoutParams: { returnTo: window.location.origin } }),
  }
}
